import React, {FC, useEffect, useRef, useState} from 'react'
import {useParams} from 'react-router'
import {BarChart3, Check, RefreshCw, X} from 'lucide-react'
import * as API from '../../api'
import IPowerBI from './IPowerBI'
import StyledCreateChart from './CreateChart.styles'

interface Props {
  chartData: any
  powerBIData?: IPowerBI
  onChartCreated: Function
  onChartUpdated: Function
  onClose: Function
}

interface GroupData {
  id: string
  name: string
}

interface ReportData {
  id: string
  name: string
  datasetId?: string
}

const CURRENT_TENANT = 'ee054992-2523-4049-b698-893495ad0f35'
const TENANT_NAMES: Record<string, string> = {
  [CURRENT_TENANT]: 'Versão Nova (Atual)',
  '4ae10e6c-5736-48bc-9b88-8f745b2c517d': 'Versão Antiga',
}
const tenantName = (id: string) => TENANT_NAMES[id] || id

const CreateChart: FC<Props> = (props) => {
  const edit = !!props.chartData
  const {dashboardId} = useParams()
  const initialSelection = useRef({
    tenant: props.chartData?.tenant_id || props.powerBIData?.tenant_id || '',
    group: props.chartData?.group_id || props.powerBIData?.groupId || '',
    report: props.chartData?.report_id || props.powerBIData?.report_id || '',
  })
  const mounted = useRef(false)
  const [chart, setChart] = useState<string>(
    props.chartData
      ? initialSelection.current.tenant
        ? 'powerbi'
        : props.chartData.chart
      : 'powerbi'
  )
  const [query, setQuery] = useState<string>(props.chartData?.query || '')
  const [title, setTitle] = useState<string>(props.chartData?.title || '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const locked = saving || saved
  const [warningMessage, setWarningMessage] = useState('')

  const [tenants, setTenants] = useState<string[]>([])
  const [tenantId, setTenant] = useState('')
  const [loadingTenants, setLoadingTenants] = useState(true)
  const [tenantError, setTenantError] = useState('')
  const [tenantReload, setTenantReload] = useState(0)

  const [groups, setGroups] = useState<GroupData[]>([])
  const [groupId, setGroup] = useState('')
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [groupError, setGroupError] = useState('')
  const [groupReload, setGroupReload] = useState(0)

  const [reports, setReports] = useState<ReportData[]>([])
  const [reportId, setReport] = useState('')
  const [loadingReports, setLoadingReports] = useState(false)
  const [reportError, setReportError] = useState('')
  const [reportReload, setReportReload] = useState(0)

  const currentGroup = groups.find((group) => group.id === groupId)
  const currentReport = reports.find((report) => report.id === reportId)
  const powerBIReady = !!(
    tenantId &&
    currentGroup &&
    currentReport?.datasetId &&
    !loadingTenants &&
    !loadingGroups &&
    !loadingReports &&
    !tenantError &&
    !groupError &&
    !reportError
  )
  const canSave = chart === 'powerbi' ? powerBIReady : !!(title.trim() && query.trim())

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    let active = true
    async function loadTenants() {
      setLoadingTenants(true)
      setTenantError('')
      try {
        const response = await API.loadTenants()
        if (!response.success || !Array.isArray(response.data)) throw new Error()
        if (!active) return
        const available = response.data.filter(
          (id: unknown): id is string => typeof id === 'string' && !!id
        )
        const savedTenant = initialSelection.current.tenant
        const options = Array.from(
          new Set<string>(savedTenant ? [...available, savedTenant] : available)
        )
        options.sort((a, b) => (a === CURRENT_TENANT ? -1 : b === CURRENT_TENANT ? 1 : 0))
        setTenants(options)
        setTenant(
          (selected) =>
            selected ||
            savedTenant ||
            (options.includes(CURRENT_TENANT) ? CURRENT_TENANT : options[0] || '')
        )
      } catch {
        if (active)
          setTenantError('Não foi possível carregar as versões do Power BI. Tente novamente.')
      } finally {
        if (active) setLoadingTenants(false)
      }
    }
    loadTenants()
    return () => {
      active = false
    }
  }, [tenantReload])

  useEffect(() => {
    let active = true
    setGroups([])
    setGroup('')
    setReports([])
    setReport('')
    setGroupError('')
    setReportError('')
    setLoadingGroups(!!tenantId)
    if (tenantId) {
      const loadGroups = async () => {
        try {
          const response = await API.loadGroups(tenantId)
          if (!response.success || !Array.isArray(response.data)) throw new Error()
          if (!active) return
          const available: GroupData[] = response.data
          const savedGroup =
            initialSelection.current.tenant === tenantId ? initialSelection.current.group : ''
          setGroups(available)
          setGroup(
            savedGroup
              ? available.find((group) => group.id === savedGroup)?.id || ''
              : available.length === 1
              ? available[0].id
              : ''
          )
        } catch {
          if (active)
            setGroupError('Não foi possível carregar os grupos desta versão. Tente novamente.')
        } finally {
          if (active) setLoadingGroups(false)
        }
      }
      loadGroups()
    }
    return () => {
      active = false
    }
  }, [tenantId, groupReload])

  useEffect(() => {
    let active = true
    setReports([])
    setReport('')
    setReportError('')
    setLoadingReports(!!(tenantId && groupId))
    if (tenantId && groupId) {
      const loadReports = async () => {
        try {
          const response = await API.loadReports(tenantId, groupId)
          if (!response.success || !Array.isArray(response.data)) throw new Error()
          if (!active) return
          const available: ReportData[] = response.data
          const savedReport =
            initialSelection.current.tenant === tenantId &&
            initialSelection.current.group === groupId
              ? initialSelection.current.report
              : ''
          setReports(available)
          setReport(
            savedReport
              ? available.find((report) => report.id === savedReport)?.id || ''
              : available.length === 1
              ? available[0].id
              : ''
          )
        } catch {
          if (active)
            setReportError('Não foi possível carregar os relatórios deste grupo. Tente novamente.')
        } finally {
          if (active) setLoadingReports(false)
        }
      }
      loadReports()
    }
    return () => {
      active = false
    }
  }, [tenantId, groupId, reportReload])

  function changeTenant(id: string) {
    initialSelection.current = {tenant: '', group: '', report: ''}
    setTenant(id)
    setGroup('')
    setGroups([])
    setReport('')
    setReports([])
    setWarningMessage('')
  }

  function changeGroup(id: string) {
    initialSelection.current = {tenant: '', group: '', report: ''}
    setGroup(id)
    setReport('')
    setReports([])
    setWarningMessage('')
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (locked || !canSave) return
    setSaving(true)
    setWarningMessage('')
    try {
      let response
      if (chart === 'powerbi' && currentGroup && currentReport?.datasetId) {
        response = edit
          ? await API.updatePowerBI(props.chartData.guid, {
              tenant_id: tenantId,
              datasets: currentReport.datasetId,
              group_id: currentGroup.id,
              report_id: currentReport.id,
            })
          : await API.createPowerBI(
              currentReport.datasetId,
              tenantId,
              currentGroup.id,
              currentReport.id,
              dashboardId as string
            )
      } else {
        response = edit
          ? await API.updateChart(props.chartData.guid, {chart, query, title})
          : await API.createChart(chart, query, title, dashboardId as string)
      }
      if (!mounted.current) return
      if (!response.success) {
        setWarningMessage(
          response.error?.message ||
            'Não foi possível salvar o gráfico. Confira a seleção e tente novamente.'
        )
        return
      }
      setSaved(true)
      try {
        await (edit ? props.onChartUpdated() : props.onChartCreated())
      } catch {
        if (mounted.current)
          setWarningMessage(
            'O gráfico foi salvo, mas não foi possível atualizar o dashboard. Feche esta janela e atualize a página.'
          )
      }
    } catch {
      if (mounted.current)
        setWarningMessage(
          'Não foi possível salvar o gráfico. Verifique sua conexão e tente novamente.'
        )
    } finally {
      if (mounted.current) setSaving(false)
    }
  }

  return (
    <StyledCreateChart onSubmit={onSubmit} aria-labelledby='create-chart-title'>
      <header className='chart-header'>
        <span className='chart-icon'>
          <BarChart3 size={24} aria-hidden='true' />
        </span>
        <div>
          <h2 id='create-chart-title'>{edit ? 'Editar gráfico' : 'Novo gráfico'}</h2>
          <p>
            {chart === 'powerbi'
              ? 'Conecte um relatório do Power BI ao seu dashboard.'
              : 'Configure os dados e a apresentação do seu gráfico.'}
          </p>
        </div>
        <button
          type='button'
          className='close-button'
          aria-label='Fechar modal'
          disabled={saving}
          onClick={() => props.onClose()}
        >
          <X size={22} aria-hidden='true' />
        </button>
      </header>

      <div className='chart-body'>
        <div className='chart-type'>
          <label htmlFor='chart-type'>Tipo de gráfico</label>
          <select
            id='chart-type'
            className='form-select'
            value={chart}
            disabled={locked}
            onChange={(event) => {
              setChart(event.target.value)
              setWarningMessage('')
            }}
          >
            <option value='powerbi'>Power BI</option>
            <option value='bar'>Barras</option>
            <option value='hbar'>Barras Horizontais</option>
            <option value='pie'>Pizza</option>
          </select>
        </div>

        {chart === 'powerbi' ? (
          <>
            <fieldset className='chart-section' disabled={locked || loadingTenants}>
              <legend>
                <span className='step-number'>1</span> Versão do Power BI
              </legend>
              <p className='section-description'>
                Use a versão atual para novos gráficos ou selecione a antiga para acessar seus
                relatórios anteriores.
              </p>
              {loadingTenants ? (
                <p className='field-status' role='status'>
                  Carregando versões...
                </p>
              ) : (
                <div className='tenant-options'>
                  {tenants.map((id) => (
                    <label
                      key={id}
                      className={`tenant-option ${tenantId === id ? 'is-selected' : ''}`}
                    >
                      <input
                        type='radio'
                        name='powerbi-tenant'
                        value={id}
                        aria-label={tenantName(id)}
                        checked={tenantId === id}
                        onChange={() => changeTenant(id)}
                      />
                      <span className='tenant-copy'>
                        <span className='tenant-name'>{tenantName(id)}</span>
                        <span className='tenant-description'>
                          {id === CURRENT_TENANT
                            ? 'Padrão para novos gráficos'
                            : id in TENANT_NAMES
                            ? 'Relatórios da versão anterior'
                            : 'Versão vinculada ao gráfico'}
                        </span>
                      </span>
                      {id === CURRENT_TENANT && <span className='current-badge'>Atual</span>}
                    </label>
                  ))}
                </div>
              )}
              {!loadingTenants && !tenantError && !tenants.length && (
                <p className='field-status' role='status'>
                  Nenhuma versão disponível.
                </p>
              )}
              {tenantError && (
                <p className='field-error' role='alert'>
                  {tenantError}
                </p>
              )}
              {!loadingTenants && (tenantError || !tenants.length) && (
                <button
                  type='button'
                  className='retry-button'
                  onClick={() => setTenantReload((value) => value + 1)}
                >
                  Tentar novamente
                </button>
              )}
              {tenantId && (
                <details className='tenant-details'>
                  <summary>Ver Tenant ID</summary>
                  <code>{tenantId}</code>
                </details>
              )}
            </fieldset>

            <section className='chart-section' aria-labelledby='group-heading'>
              <div className='section-heading'>
                <h3 id='group-heading'>
                  <span className='step-number'>2</span> Grupo de trabalho
                </h3>
                <button
                  type='button'
                  className='refresh-button'
                  aria-label='Atualizar grupos'
                  disabled={!tenantId || loadingTenants || loadingGroups || locked}
                  onClick={() => {
                    initialSelection.current = {tenant: tenantId, group: groupId, report: reportId}
                    setGroup('')
                    setReport('')
                    setWarningMessage('')
                    setGroupReload((value) => value + 1)
                  }}
                >
                  <RefreshCw size={14} aria-hidden='true' /> Atualizar
                </button>
              </div>
              <p className='section-description'>
                Escolha o grupo que contém o relatório que você quer adicionar.
              </p>
              <label className='visually-hidden' htmlFor='powerbi-group'>
                Grupo de trabalho
              </label>
              <select
                id='powerbi-group'
                className='form-select'
                value={groupId}
                disabled={!tenantId || loadingTenants || loadingGroups || locked || !groups.length}
                onChange={(event) => changeGroup(event.target.value)}
                aria-describedby='group-status'
                aria-busy={loadingGroups}
              >
                <option value=''>
                  {loadingGroups
                    ? 'Carregando grupos...'
                    : !tenantId
                    ? 'Selecione uma versão primeiro'
                    : 'Selecione um grupo de trabalho'}
                </option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              <p
                id='group-status'
                className={groupError ? 'field-error' : 'field-status'}
                role={groupError ? 'alert' : 'status'}
              >
                {groupError ||
                  (loadingGroups
                    ? 'Buscando os grupos desta versão...'
                    : !tenantId
                    ? 'Os grupos serão carregados após a seleção da versão.'
                    : !groups.length
                    ? 'Nenhum grupo encontrado nesta versão. Você pode atualizar a lista ou escolher outra versão.'
                    : `${groups.length} ${
                        groups.length === 1 ? 'grupo disponível' : 'grupos disponíveis'
                      }`)}
              </p>
              {groupError && (
                <button
                  type='button'
                  className='retry-button'
                  disabled={locked}
                  onClick={() => setGroupReload((value) => value + 1)}
                >
                  Tentar novamente
                </button>
              )}
            </section>

            <section className='chart-section' aria-labelledby='report-heading'>
              <div className='section-heading'>
                <h3 id='report-heading'>
                  <span className='step-number'>3</span> Relatório
                </h3>
                <button
                  type='button'
                  className='refresh-button'
                  aria-label='Atualizar relatórios'
                  disabled={!groupId || loadingReports || loadingGroups || locked}
                  onClick={() => {
                    initialSelection.current = {tenant: tenantId, group: groupId, report: reportId}
                    setReport('')
                    setWarningMessage('')
                    setReportReload((value) => value + 1)
                  }}
                >
                  <RefreshCw size={14} aria-hidden='true' /> Atualizar
                </button>
              </div>
              <p className='section-description'>
                O relatório selecionado será exibido no dashboard.
              </p>
              <label className='visually-hidden' htmlFor='powerbi-report'>
                Relatório
              </label>
              <select
                id='powerbi-report'
                className='form-select'
                value={reportId}
                disabled={!groupId || loadingReports || locked || !reports.length}
                onChange={(event) => {
                  setReport(event.target.value)
                  setWarningMessage('')
                }}
                aria-describedby='report-status'
                aria-busy={loadingReports}
              >
                <option value=''>
                  {loadingReports
                    ? 'Carregando relatórios...'
                    : !groupId
                    ? 'Selecione um grupo primeiro'
                    : 'Selecione um relatório'}
                </option>
                {reports.map((report) => (
                  <option key={report.id} value={report.id}>
                    {report.name}
                  </option>
                ))}
              </select>
              <p
                id='report-status'
                className={reportError ? 'field-error' : 'field-status'}
                role={reportError ? 'alert' : 'status'}
              >
                {reportError ||
                  (loadingReports
                    ? 'Buscando os relatórios deste grupo...'
                    : !groupId
                    ? 'Os relatórios serão carregados após a seleção do grupo.'
                    : !reports.length
                    ? 'Nenhum relatório encontrado neste grupo. Você pode atualizar a lista ou escolher outro grupo.'
                    : `${reports.length} ${
                        reports.length === 1 ? 'relatório disponível' : 'relatórios disponíveis'
                      }`)}
              </p>
              {reportError && (
                <button
                  type='button'
                  className='retry-button'
                  disabled={locked}
                  onClick={() => setReportReload((value) => value + 1)}
                >
                  Tentar novamente
                </button>
              )}
              {currentReport && !currentReport.datasetId && (
                <p className='field-error' role='alert'>
                  Este relatório não possui um conjunto de dados compatível. Selecione outro
                  relatório.
                </p>
              )}
            </section>

            {powerBIReady && (
              <div className='selection-summary'>
                <Check size={19} aria-hidden='true' />
                <div className='selection-summary-content'>
                  <strong>Pronto para {edit ? 'salvar' : 'adicionar'}</strong>
                  <dl className='selection-details'>
                    <div>
                      <dt>Versão</dt>
                      <dd>{tenantName(tenantId)}</dd>
                    </div>
                    <div>
                      <dt>Grupo</dt>
                      <dd>{currentGroup?.name}</dd>
                    </div>
                    <div>
                      <dt>Relatório</dt>
                      <dd>{currentReport?.name}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className='native-chart-fields'>
            <div>
              <label htmlFor='chart-title'>Título do gráfico</label>
              <input
                id='chart-title'
                className='form-control'
                value={title}
                placeholder='Ex.: Vendas por mês'
                disabled={locked}
                required
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor='chart-query'>Consulta SQL</label>
              <textarea
                id='chart-query'
                className='form-control query-input'
                value={query}
                placeholder='Digite a consulta que fornece os dados do gráfico'
                rows={5}
                disabled={locked}
                required
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          </div>
        )}
        {warningMessage && (
          <p className='save-error' role='alert'>
            {warningMessage}
          </p>
        )}
      </div>

      <footer className='chart-footer'>
        <p>
          {saved
            ? 'Gráfico salvo no dashboard.'
            : saving
            ? 'Salvando gráfico...'
            : canSave
            ? 'Tudo pronto. Confirme para salvar no dashboard.'
            : 'Preencha as informações para continuar.'}
        </p>
        <div className='action-buttons'>
          <button
            type='button'
            className='btn btn-light'
            disabled={saving}
            onClick={() => props.onClose()}
          >
            {saved ? 'Fechar' : 'Cancelar'}
          </button>
          <button type='submit' className='btn btn-primary' disabled={locked || !canSave}>
            {saving && (
              <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
            )}
            {saved
              ? 'Gráfico salvo'
              : saving
              ? 'Salvando...'
              : edit
              ? 'Salvar alterações'
              : 'Criar gráfico'}
          </button>
        </div>
      </footer>
    </StyledCreateChart>
  )
}

export default CreateChart
